// WS client (Day-4): /ws/phone 长连接 + 自动重连 + 主备切换 + 应用层心跳。
//
// 主备切换：
//   - 主入口 wss://ear-sgp.platform-1.xyz/ws/phone
//   - 备入口 wss://ear-hkg.platform-1.xyz/ws/phone （Day-7 才真上线，先占位）
//   - 备2  wss://ear-lax.platform-1.xyz/ws/phone （进一步备份）
//   - 单次 connect 失败 → 5s 内退避；连续 3 次同一入口失败 → 切下一个
//
// 协议帧 = Envelope（与 gateway/internal/wsproto/messages.go 对齐）：
//   { v, type, id, ts, ack_id?, payload }
//
// Day-4 该模块只跑得起来 + 收到 server_hello 即算"配对成立"，
// Day-5 会扩展 send_command / assistant_delta / assistant_done 路由。

import { signWsHandshake } from "../crypto/hmac";
import { tokenStore } from "../storage/tokens";

export type EnvelopeType =
  | "phone_hello"
  | "server_hello"
  | "heartbeat"
  | "heartbeat_ack"
  | "send_command"
  | "command_dispatched"
  | "assistant_delta"
  | "assistant_done"
  | "agent_blocked"
  | "agent_offline"
  | "agent_online"
  | "task_expectation"
  | "error";

export interface Envelope<P = unknown> {
  v: number;
  type: EnvelopeType | string;
  id: string;
  ts: number;
  ack_id?: string;
  payload?: P;
}

export type WSState = "idle" | "connecting" | "open" | "reconnecting" | "fatal";

export interface WSClientHandlers {
  onState?: (s: WSState, detail?: string) => void;
  onMessage?: (env: Envelope) => void;
  onLog?: (line: string) => void;
}

const FALLBACK_HOSTS = [
  "ear-sgp.platform-1.xyz",
  "ear-hkg.platform-1.xyz", // Day-7 才上线
  "ear-lax.platform-1.xyz"
];

const HEARTBEAT_INTERVAL_MS = 30_000;
const HEARTBEAT_MISS_LIMIT = 3;
const BACKOFFS_MS = [1000, 2000, 5000, 15000, 15000];

function genId(): string {
  return `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

function envelope<P>(type: string, payload: P, ack_id?: string): Envelope<P> {
  return {
    v: 1,
    type,
    id: genId(),
    ts: Date.now(),
    payload,
    ...(ack_id ? { ack_id } : {})
  };
}

export class WSClient {
  private ws: WebSocket | null = null;
  private state: WSState = "idle";
  private hostIdx = 0;
  private failCount = 0;
  private hbTimer: number | null = null;
  private hbMissed = 0;
  private reconnectTimer: number | null = null;
  private closed = false;

  constructor(private h: WSClientHandlers = {}) {}

  start(): void {
    this.closed = false;
    void this.connect();
  }

  /** Day-10: 用户点 ConnectionBanner / visibilitychange 触发的主动重连。 */
  reconnect(): void {
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.ws) {
      try {
        this.ws.close(4002, "manual reconnect");
      } catch {
        /* ignore */
      }
      this.ws = null;
    }
    if (!this.closed) {
      this.failCount = 0;
      void this.connect();
    }
  }

  stop(): void {
    this.closed = true;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    if (this.hbTimer) {
      clearInterval(this.hbTimer);
      this.hbTimer = null;
    }
    if (this.ws) {
      this.ws.close(1000, "client stop");
      this.ws = null;
    }
    this.setState("idle");
  }

  send<P>(type: string, payload: P, ack_id?: string): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.h.onLog?.(`[ws] drop ${type}: not open`);
      return;
    }
    const env = envelope(type, payload, ack_id);
    this.ws.send(JSON.stringify(env));
  }

  /** Day-8: 发送预先构造好的 envelope（CliRPC 用，能控制 id 实现请求/响应配对）。 */
  sendRaw(env: Envelope): void {
    if (!this.ws || this.ws.readyState !== WebSocket.OPEN) {
      this.h.onLog?.(`[ws] drop sendRaw ${env.type}: not open`);
      throw new Error("ws not open");
    }
    this.ws.send(JSON.stringify(env));
  }

  private setState(s: WSState, detail?: string) {
    this.state = s;
    this.h.onState?.(s, detail);
  }

  private async connect() {
    if (this.closed) return;
    const ident = await tokenStore.getIdentity();
    if (!ident) {
      this.setState("fatal", "no_identity");
      return;
    }

    const host = FALLBACK_HOSTS[this.hostIdx];
    this.setState("connecting", host);

    const qs = await signWsHandshake(ident.device_id, ident.token, "/ws/phone");
    const url = `wss://${host}/ws/phone?${qs}`;
    this.h.onLog?.(`[ws] connect → ${host}`);

    let ws: WebSocket;
    try {
      ws = new WebSocket(url);
    } catch (e) {
      this.h.onLog?.(`[ws] new WebSocket() throw: ${e}`);
      this.scheduleReconnect();
      return;
    }
    this.ws = ws;

    ws.onopen = () => {
      this.failCount = 0;
      this.hbMissed = 0;
      this.setState("open", host);
      this.h.onLog?.(`[ws] open ${host}`);
      // 发 phone_hello
      this.send("phone_hello", {
        device_id: ident.device_id,
        alias: ident.alias,
        version: "0.4.0-day4",
        ua: navigator.userAgent
      });
      this.startHeartbeat();
    };

    ws.onmessage = (ev) => {
      let env: Envelope;
      try {
        env = JSON.parse(ev.data);
      } catch {
        this.h.onLog?.(`[ws] non-JSON frame ignored`);
        return;
      }
      if (env.type === "heartbeat_ack") {
        this.hbMissed = 0;
      }
      this.h.onMessage?.(env);
    };

    ws.onerror = () => {
      this.h.onLog?.(`[ws] error on ${host}`);
    };

    ws.onclose = (ev) => {
      this.h.onLog?.(`[ws] close ${ev.code} ${ev.reason || ""} on ${host}`);
      this.stopHeartbeat();
      this.ws = null;
      if (this.closed) return;

      this.failCount += 1;
      // 1006 / 4xxx 错误：极可能是入口 down，尝试切下一个 host
      if (this.failCount >= 3 && this.hostIdx < FALLBACK_HOSTS.length - 1) {
        this.hostIdx += 1;
        this.failCount = 0;
        this.h.onLog?.(`[ws] switch to ${FALLBACK_HOSTS[this.hostIdx]}`);
      }
      this.scheduleReconnect();
    };
  }

  private scheduleReconnect() {
    if (this.closed) return;
    const wait = BACKOFFS_MS[Math.min(this.failCount, BACKOFFS_MS.length - 1)];
    this.setState("reconnecting", `${wait}ms`);
    this.reconnectTimer = window.setTimeout(() => {
      this.reconnectTimer = null;
      void this.connect();
    }, wait);
  }

  private startHeartbeat() {
    this.stopHeartbeat();
    this.hbTimer = window.setInterval(() => {
      if (!this.ws || this.ws.readyState !== WebSocket.OPEN) return;
      this.send("heartbeat", { last_activity_ts: Math.floor(Date.now() / 1000) });
      this.hbMissed += 1;
      if (this.hbMissed > HEARTBEAT_MISS_LIMIT) {
        this.h.onLog?.(`[ws] missed too many heartbeat_ack, force close`);
        this.ws?.close(4001, "hb_miss");
      }
    }, HEARTBEAT_INTERVAL_MS);
  }

  private stopHeartbeat() {
    if (this.hbTimer) {
      clearInterval(this.hbTimer);
      this.hbTimer = null;
    }
  }
}
